import React, { useState, useEffect } from 'react';
import CalmiCharacter from '../pet/components/CalmiCharacter';
import { getEmotions, getEmotionReasons, GRATITUDE_SUGGESTIONS, DAILY_GOALS, DIARY_PROMPTS } from '../../shared/constants';
import { useGender, Gender } from '../../shared/hooks/useGender';
import { Card, NavigationBar, Button, ThemedScreen } from '../../shared/components';
import { storageService } from '../../shared/services/storageService';
import { notificationService } from '../../shared/services/notificationService';

interface DailyGoal {
  id: string;
  label: string;
  option: string;
  completed: boolean;
  date: string;
}

const NEGATIVE_EMOTIONS = ['sad', 'anxious', 'angry', 'tired'];

const DiaryScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ userName, onNavigate }) => {
  const { gender } = useGender();
  const [selectedSection, setSelectedSection] = useState<'emotion' | 'gratitude' | 'goal' | 'history' | 'thoughts' | null>(null);
  const [thoughtsText, setThoughtsText] = useState('');
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [gratitudeItems, setGratitudeItems] = useState<{ happy: string; person: string; fun: string }>({
    happy: '',
    person: '',
    fun: ''
  });
  const [selectedGoalCategory, setSelectedGoalCategory] = useState<string | null>(null);
  const [selectedGoalOption, setSelectedGoalOption] = useState<string | null>(null);
  const [dailyGoals, setDailyGoals] = useState<DailyGoal[]>([]);
  const [goalHistory, setGoalHistory] = useState<DailyGoal[]>([]);
  const [streak, setStreak] = useState(0);
  const [today, setToday] = useState('');
  const [historyPeriod, setHistoryPeriod] = useState<'week' | 'month' | 'months'>('week');

  useEffect(() => {
    const todayDate = new Date().toDateString();
    setToday(todayDate);
    
    // Load streak from storage
    setStreak(storageService.getStreak());
    
    // Load today's goals from storage
    const todayGoals = storageService.getGoals().filter(g => 
      g.date.split('T')[0] === new Date().toISOString().split('T')[0]
    );
    setDailyGoals(todayGoals);
    
    // Load completed goals for history
    setGoalHistory(storageService.getCompletedGoals());
    
    // Reset goals at midnight
    const checkMidnight = () => {
      const newToday = new Date().toDateString();
      if (newToday !== todayDate) {
        setDailyGoals([]);
        setToday(newToday);
      }
    };

    const interval = setInterval(checkMidnight, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const handleEmotionSelect = (emotionId: string) => {
    setSelectedEmotion(emotionId);
    setSelectedReason(null);
  };

  const handleReasonSelect = (reason: string) => {
    if (reason === 'Otro') {
      setSelectedReason('Otro');
    } else {
      setSelectedReason(reason);
    }
  };

  const getEmotionReasonsList = () => {
    if (!selectedEmotion) return [];
    return getEmotionReasons(gender)[selectedEmotion] || [];
  };

  const handleSaveEmotion = () => {
    if (selectedEmotion && selectedReason) {
      const reasonToSave = selectedReason === 'Otro' ? customReason : selectedReason;
      storageService.saveEmotion(selectedEmotion, reasonToSave);
      
      // Generar alerta si la emoción es preocupante
      const childProfile = localStorage.getItem('calmi_child_profile');
      const childName = childProfile ? JSON.parse(childProfile).name : 'El menor';
      notificationService.generateAlert(childName, selectedEmotion);
    }
    setSelectedSection(null);
    setSelectedEmotion(null);
    setSelectedReason(null);
    setCustomReason('');
  };

  const handleSaveGratitude = () => {
    if (gratitudeItems.happy || gratitudeItems.person || gratitudeItems.fun) {
      storageService.saveGratitude(
        gratitudeItems.happy,
        gratitudeItems.person,
        gratitudeItems.fun
      );
    }
    setSelectedSection(null);
    setGratitudeItems({ happy: '', person: '', fun: '' });
  };

  const handleSaveThoughts = () => {
    if (thoughtsText.trim()) {
      storageService.saveThought(thoughtsText);
      setThoughtsText('');
      setSelectedSection(null);
      alert('¡Pensamientos guardados!');
    }
  };

  const handleGoalCategorySelect = (categoryId: string) => {
    setSelectedGoalCategory(categoryId);
    setSelectedGoalOption(null);
  };

  const handleGoalOptionSelect = (option: string) => {
    setSelectedGoalOption(option);
  };

  const handleAddGoal = () => {
    if (selectedGoalCategory && selectedGoalOption && dailyGoals.length < 2) {
      const label = DAILY_GOALS[selectedGoalCategory as keyof typeof DAILY_GOALS].label;
      const newGoal: DailyGoal = {
        id: Date.now().toString(),
        label,
        option: selectedGoalOption,
        completed: false,
        date: today
      };
      setDailyGoals([...dailyGoals, newGoal]);
      storageService.saveGoal(label, selectedGoalOption, false);
      setSelectedGoalCategory(null);
      setSelectedGoalOption(null);
    }
  };

  const handleToggleGoalComplete = (goalId: string) => {
    const updatedGoals = dailyGoals.map(goal => 
      goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
    );
    setDailyGoals(updatedGoals);
    
    const goal = dailyGoals.find(g => g.id === goalId);
    if (goal) {
      storageService.updateGoalCompletion(goalId, !goal.completed);
      if (!goal.completed) {
        setGoalHistory([...goalHistory, { ...goal, completed: true }]);
        setStreak(storageService.getStreak());
      }
    }
  };

  const renderEmotionSection = () => (
    <div className="text-center">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">¿Cómo te sientes hoy?</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-6">Elige el emoji que mejor describa cómo te sientes</p>
      
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
        {getEmotions(gender).map((emotion) => (
          <button
            key={emotion.id}
            onClick={() => handleEmotionSelect(emotion.id)}
            className={`p-3 sm:p-4 rounded-2xl transition-all ${
              selectedEmotion === emotion.id ? 'bg-purple-200 ring-4 ring-purple-400' : 'bg-white hover:bg-purple-100'
            }`}
          >
            <div className="text-3xl sm:text-4xl mb-1">{emotion.emoji}</div>
            <div className="text-xs sm:text-sm text-gray-700">{emotion.label}</div>
          </button>
        ))}
      </div>

      {selectedEmotion && (
        <div className="mb-6">
          <p className="text-sm sm:text-base text-gray-700 mb-3">¿Por qué te sientes así?</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {getEmotionReasonsList().map((reason: string) => (
              <button
                key={reason}
                onClick={() => handleReasonSelect(reason)}
                className={`p-2 sm:p-3 rounded-xl text-xs sm:text-sm transition-all ${
                  selectedReason === reason ? 'bg-blue-200 ring-2 ring-blue-400' : 'bg-white hover:bg-blue-100'
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
          {selectedReason === 'Otro' && (
            <input
              type="text"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Escribe aquí..."
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 mb-4 text-sm"
            />
          )}
        </div>
      )}

      <div className="flex gap-2 justify-center">
        <Button onClick={handleSaveEmotion} disabled={!selectedEmotion}>
          Guardar
        </Button>
        <Button onClick={() => setSelectedSection(null)} variant="secondary">
          ← Volver
        </Button>
      </div>
    </div>
  );

  const renderGratitudeSection = () => (
    <div className="text-center">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">3 Cosas Buenas 🌟</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-6">Escribe 3 cosas buenas de hoy</p>

      <Card className="mb-4 p-4 text-left">
        <p className="text-sm font-semibold text-gray-800 mb-2">Algo que te hizo feliz hoy:</p>
        <input
          type="text"
          value={gratitudeItems.happy}
          onChange={(e) => setGratitudeItems({ ...gratitudeItems, happy: e.target.value })}
          placeholder="Ej: Comí algo rico"
          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 mb-2 text-sm"
        />
        <div className="flex flex-wrap gap-1">
          {GRATITUDE_SUGGESTIONS.happy.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setGratitudeItems({ ...gratitudeItems, happy: suggestion })}
              className="text-xs bg-pink-100 hover:bg-pink-200 px-2 py-1 rounded-full"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </Card>

      <Card className="mb-4 p-4 text-left">
        <p className="text-sm font-semibold text-gray-800 mb-2">Alguien que te cae bien:</p>
        <input
          type="text"
          value={gratitudeItems.person}
          onChange={(e) => setGratitudeItems({ ...gratitudeItems, person: e.target.value })}
          placeholder="Ej: Mi mamá"
          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 mb-2 text-sm"
        />
        <div className="flex flex-wrap gap-1">
          {GRATITUDE_SUGGESTIONS.person.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setGratitudeItems({ ...gratitudeItems, person: suggestion })}
              className="text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-full"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </Card>

      <Card className="mb-4 p-4 text-left">
        <p className="text-sm font-semibold text-gray-800 mb-2">Algo divertido que hiciste:</p>
        <input
          type="text"
          value={gratitudeItems.fun}
          onChange={(e) => setGratitudeItems({ ...gratitudeItems, fun: e.target.value })}
          placeholder="Ej: Jugué fútbol"
          className="w-full p-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 mb-2 text-sm"
        />
        <div className="flex flex-wrap gap-1">
          {GRATITUDE_SUGGESTIONS.fun.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setGratitudeItems({ ...gratitudeItems, fun: suggestion })}
              className="text-xs bg-green-100 hover:bg-green-200 px-2 py-1 rounded-full"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </Card>

      <div className="flex gap-2 justify-center">
        <Button onClick={handleSaveGratitude}>
          Guardar
        </Button>
        <Button onClick={() => setSelectedSection(null)} variant="secondary">
          ← Volver
        </Button>
      </div>
    </div>
  );

  const renderThoughtsSection = () => (
    <div className="text-center">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Escribe tus pensamientos 📝</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-6">¿Qué te preocupa? ¿Qué te hace feliz? Escribe todo lo que quieras.</p>

      <Card className="mb-6 p-4">
        <textarea
          value={thoughtsText}
          onChange={(e) => setThoughtsText(e.target.value)}
          placeholder="Escribe aquí tus pensamientos..."
          className="w-full h-48 p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 text-sm"
        />
      </Card>

      <div className="flex gap-2 justify-center">
        <Button onClick={handleSaveThoughts} disabled={!thoughtsText.trim()}>
          Guardar
        </Button>
        <Button onClick={() => setSelectedSection(null)} variant="secondary">
          ← Volver
        </Button>
      </div>
    </div>
  );

  const renderGoalSection = () => (
    <div className="text-center">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Mi Meta de Hoy 🎯</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-2">Elige hasta 2 metas para hoy</p>
      
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-2xl">🔥</span>
        <span className="text-lg font-bold text-orange-500">{streak} días seguidos</span>
      </div>

      {!selectedGoalCategory ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {Object.keys(DAILY_GOALS).map((key) => {
              const goal = DAILY_GOALS[key as keyof typeof DAILY_GOALS];
              return (
                <button
                  key={key}
                  onClick={() => handleGoalCategorySelect(key)}
                  className={`p-4 rounded-2xl transition-all ${
                    selectedGoalCategory === key ? 'bg-purple-200 ring-4 ring-purple-400' : 'bg-white hover:bg-purple-100'
                  }`}
                >
                  <div className="text-3xl sm:text-4xl mb-2">{goal.icon}</div>
                  <div className="text-xs sm:text-sm text-gray-700 font-medium">{goal.label}</div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-4">
            {DAILY_GOALS[selectedGoalCategory as keyof typeof DAILY_GOALS].label} - Elige una opción:
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {DAILY_GOALS[selectedGoalCategory as keyof typeof DAILY_GOALS].options.map((option) => (
              <button
                key={option}
                onClick={() => handleGoalOptionSelect(option)}
                className={`p-3 rounded-xl transition-all ${
                  selectedGoalOption === option ? 'bg-blue-200 ring-2 ring-blue-400' : 'bg-white hover:bg-blue-100'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          {selectedGoalOption && (
            <Button onClick={handleAddGoal} className="mb-4" disabled={dailyGoals.length >= 2}>
              {dailyGoals.length >= 2 ? 'Máximo 2 metas por día' : 'Agregar meta'}
            </Button>
          )}
          <Button onClick={() => setSelectedGoalCategory(null)} variant="secondary">
            Volver a categorías
          </Button>
        </>
      )}

      {dailyGoals.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-800 mb-3">Tus metas de hoy:</h3>
          {dailyGoals.map((goal) => (
            <Card key={goal.id} className="mb-3 p-4 flex items-center justify-between">
              <div className="text-left">
                <div className="font-medium text-gray-800">{goal.label}</div>
                <div className="text-sm text-gray-600">{goal.option}</div>
              </div>
              <button
                onClick={() => handleToggleGoalComplete(goal.id)}
                className={`text-3xl ${goal.completed ? 'opacity-100' : 'opacity-50'}`}
              >
                {goal.completed ? '✅' : '⬜'}
              </button>
            </Card>
          ))}
        </div>
      )}

      <div className="flex gap-2 justify-center mt-6">
        <Button onClick={() => setSelectedSection(null)} variant="secondary">
          ← Volver
        </Button>
      </div>
    </div>
  );

  const getPeriodDays = () => {
    switch (historyPeriod) {
      case 'week': return 7;
      case 'month': return 30;
      case 'months': return 90;
      default: return 7;
    }
  };

  const getPeriodLabel = () => {
    switch (historyPeriod) {
      case 'week': return 'Esta semana';
      case 'month': return 'Este mes';
      case 'months': return 'Últimos 3 meses';
      default: return 'Esta semana';
    }
  };

  const renderHistorySection = () => {
    const days = getPeriodDays();
    const emotions = storageService.getEmotionsByPeriod(days);
    const emotionStats = storageService.getEmotionStats(days);
    const completedGoals = storageService.getCompletedGoals().filter(g => {
      const goalDate = new Date(g.date);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      return goalDate >= cutoffDate;
    });

    const getEmotionEmoji = (emotionId: string) => {
      const emotion = getEmotions(gender).find(e => e.id === emotionId);
      return emotion ? emotion.emoji : '😊';
    };

    const getWeekEmotions = () => {
      const weekData = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayEmotion = emotions.find(e => e.date.split('T')[0] === dateStr);
        weekData.push({
          day: date.toLocaleDateString('es', { weekday: 'short' }).toUpperCase(),
          emoji: dayEmotion ? getEmotionEmoji(dayEmotion.emotion) : '😐'
        });
      }
      return weekData;
    };

    const totalEmotions = Object.keys(emotionStats).reduce((sum, key) => sum + (emotionStats[key] || 0), 0);
    const emotionPercentages = Object.keys(emotionStats).map((emotionId) => ({
      emotionId,
      count: emotionStats[emotionId],
      percentage: totalEmotions > 0 ? Math.round((emotionStats[emotionId] / totalEmotions) * 100) : 0
    })).sort((a, b) => b.count - a.count).slice(0, 3);

    return (
      <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Mi Historial 📊</h2>
        
        <div className="flex justify-center gap-2 mb-4">
          <button
            onClick={() => setHistoryPeriod('week')}
            className={`px-3 py-1 rounded-full text-sm ${historyPeriod === 'week' ? 'bg-purple-500 text-white' : 'bg-gray-200'}`}
          >
            Semana
          </button>
          <button
            onClick={() => setHistoryPeriod('month')}
            className={`px-3 py-1 rounded-full text-sm ${historyPeriod === 'month' ? 'bg-purple-500 text-white' : 'bg-gray-200'}`}
          >
            Mes
          </button>
          <button
            onClick={() => setHistoryPeriod('months')}
            className={`px-3 py-1 rounded-full text-sm ${historyPeriod === 'months' ? 'bg-purple-500 text-white' : 'bg-gray-200'}`}
          >
            3 Meses
          </button>
        </div>

        <Card className="mb-6 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">{getPeriodLabel()}</h3>
          {historyPeriod === 'week' ? (
            <div className="grid grid-cols-7 gap-1 mb-4">
              {getWeekEmotions().map((data, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-gray-600 mb-1">{data.day}</div>
                  <div className="text-2xl">{data.emoji}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600 mb-4">
              {emotions.length} {emotions.length === 1 ? 'emoción registrada' : 'emociones registradas'} en este período
            </p>
          )}
          {emotions.length > 0 && (() => {
            const recentEmotions = emotions.slice(-3);
            const recentNegativeCount = recentEmotions.filter(e => NEGATIVE_EMOTIONS.indexOf(e.emotion) !== -1).length;
            if (recentEmotions.length >= 3 && recentNegativeCount >= 3) {
              return (
                <p className="text-sm text-orange-700 font-medium bg-orange-50 p-2 rounded-lg">
                  🧡 Últimamente has sentido emociones difíciles. Está bien pedir ayuda. No tienes que afrontar esto sol{gender === 'female' ? 'a' : 'o'}.
                </p>
              );
            }
            return (
              <p className="text-sm text-green-600 font-medium">
                {emotionPercentages[0]?.percentage > 50 ? '¡Estás muy bien!' : 'Sigue así, cada día cuenta'}
              </p>
            );
          })()}
        </Card>

        <Card className="mb-6 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Emociones más comunes</h3>
          {emotionPercentages.length > 0 ? (
            <div className="space-y-2">
              {emotionPercentages.map(({ emotionId, percentage }) => (
                <div key={emotionId} className="flex items-center gap-2">
                  <span className="text-2xl">{getEmotionEmoji(emotionId)}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4">
                    <div className="bg-purple-400 h-4 rounded-full" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <span className="text-sm text-gray-600">{percentage}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">Aún no has registrado emociones</p>
          )}
        </Card>

        <Card className="mb-6 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Historial de Metas</h3>
          {completedGoals.length > 0 ? (
            <div className="space-y-2">
              {completedGoals.slice(-5).reverse().map((goal) => (
                <div key={goal.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-800">{goal.label}</div>
                    <div className="text-xs text-gray-600">{goal.option}</div>
                  </div>
                  <div className="text-xl">✅</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">Aún no has completado metas en este período</p>
          )}
        </Card>

        <Button onClick={() => setSelectedSection(null)} variant="secondary">
          Volver
        </Button>
      </div>
    );
  };

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8 pb-24">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => selectedSection ? setSelectedSection(null) : onNavigate('home')} 
            className="p-2 sm:p-3 bg-white rounded-full shadow-md hover:shadow-lg transition-all"
          >
            <span className="text-xl sm:text-2xl">←</span>
          </button>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
            {selectedSection ? 'Actividad' : 'Mi Diario'}
          </h1>
          <div className="w-10"></div>
        </div>

        {!selectedSection ? (
          <>
            {/* Description */}
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6">
              {DIARY_PROMPTS[Math.floor(Math.random() * DIARY_PROMPTS.length)]}
            </p>

            {/* Calmi Character */}
            <div className="flex justify-center mb-6">
              <CalmiCharacter size="medium" />
            </div>

            {/* Diary Options */}
            <div className="space-y-3 mb-20">
              <Card
                className="p-4 hover:shadow-lg transition-all cursor-pointer flex items-center gap-4"
                onClick={() => setSelectedSection('emotion')}
              >
                <div className="text-4xl">😊</div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">¿Cómo te sientes hoy?</div>
                  <div className="text-sm text-gray-600">Registra tu emoción</div>
                </div>
                <div className="text-2xl">→</div>
              </Card>

              <Card
                className="p-4 hover:shadow-lg transition-all cursor-pointer flex items-center gap-4"
                onClick={() => setSelectedSection('gratitude')}
              >
                <div className="text-4xl">🌟</div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">3 Cosas Buenas</div>
                  <div className="text-sm text-gray-600">Diario de gratitud</div>
                </div>
                <div className="text-2xl">→</div>
              </Card>

              <Card
                className="p-4 hover:shadow-lg transition-all cursor-pointer flex items-center gap-4"
                onClick={() => setSelectedSection('thoughts')}
              >
                <div className="text-4xl">📝</div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">Escribe tus pensamientos</div>
                  <div className="text-sm text-gray-600">Expresa lo que sientes</div>
                </div>
                <div className="text-2xl">→</div>
              </Card>

              <Card
                className="p-4 hover:shadow-lg transition-all cursor-pointer flex items-center gap-4"
                onClick={() => setSelectedSection('goal')}
              >
                <div className="text-4xl">🎯</div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">Mi Meta de Hoy</div>
                  <div className="text-sm text-gray-600">Meta diaria gamificada</div>
                </div>
                <div className="text-2xl">→</div>
              </Card>

              <Card
                className="p-4 hover:shadow-lg transition-all cursor-pointer flex items-center gap-4"
                onClick={() => setSelectedSection('history')}
              >
                <div className="text-4xl">📊</div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">Mi Historial</div>
                  <div className="text-sm text-gray-600">Ver tu progreso</div>
                </div>
                <div className="text-2xl">→</div>
              </Card>
            </div>
          </>
        ) : (
          <div className="mb-20">
            {selectedSection === 'emotion' && renderEmotionSection()}
            {selectedSection === 'gratitude' && renderGratitudeSection()}
            {selectedSection === 'thoughts' && renderThoughtsSection()}
            {selectedSection === 'goal' && renderGoalSection()}
            {selectedSection === 'history' && renderHistorySection()}
          </div>
        )}

        {/* Bottom Navigation */}
        <NavigationBar activeItem="diary" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default DiaryScreen;
